import loadScript from '@/utils/load-script.js';


// Example opts:
// {
//   noHeaderRow: false,
//   headerWarnings: [ headers => headers.includes('name') || 'Missing <code>name</code> header' ],
//   headerErrors: [ headers => (headers.includes('email') || headers.includes('phone')) || 'Must contain at least an 'email' or 'phone' header' ],
//   parse: x => x,
//   parsers: {
//     email: x => EMAIL_REGEX.test(x) ? x : throw new Error('Invalid email address'),
//     phone: x => {
//       const digits = x.replace(/[^\d]/g, '');
//       if (digits.length < 10)
//         throw new Error('Invalid phone number');
//       if (digits.length == 10)
//         return '+' + this.hints.intlPrefix + digits;
//       else
//         return '+' + digits;
//     }
//   }
// }


function getExcelDate(v) {
  // 'v' is the number of days since 1899-12-31, minus a leap day in February 1900
  // https://www.myonlinetraininghub.com/excel-date-and-time
  return new Date(new Date('1900-01-01').valueOf() + (v - 1 - (v >= 62 ? 1 : 0)) * 86400000).toISOString().substr(0, 10);
}


function getExcelTime(v) {
  // 'v' is the fractional component of a 24-hour day (e.g. .5 == noon)
  // https://www.myonlinetraininghub.com/excel-date-and-time
  const hh = Math.floor(24 * v);
  const mm = Math.floor(24 * 60 * v % 60);
  const ss = Math.floor(24 * 60 * 60 * v % 60);
  return String(hh).padStart(2, '0') + ':' + String(mm).padStart(2, '0') + ':' + (ss < 10 ? '0' : '') + ss;
}


function getExcelValue(cell) {
  if (!cell || cell.t == 'stub')
    return '';
  if ((cell.t == 'n') && (String(cell.v) != cell.w)) {
    try {
      if (cell.v % 1 == 0)
        // Date only
        return getExcelDate(cell.v);
      // Make sure to truncate to avoid off-by-one errors related to floats when computing the time component
      const decimal = parseFloat((cell.v % 1).toPrecision(8));
      if (cell.v < 1)
        // Time only
        return getExcelTime(decimal);
      // Datetime
      return new Date(getExcelDate(Math.floor(cell.v)) + 'T' + getExcelTime(decimal)).toISOString();
    } catch (e) {
    }
  }
  return String(cell.v).trim();
}


export default {
  data() {
    return {
      readSpreadsheetStatus: this.$track('readSpreadsheet')
    };
  },

  methods: {
    async readSpreadsheet(file, opts={}) {
      opts = Object.assign({ headersCaseSensitive: true, headerWarnings: [], headerErrors: [], parsers: {} }, opts)

      const headers = [];
      const items = [];
      let parsers = null;

      const addHeaders = async keys => {
        let nNoHeader = 0;
        for (const key of keys.map(x => x && (opts.headersCaseSensitive ? x : x.toLowerCase()).trim())) {
          if (!key) {
            nNoHeader++;
            continue;
          }
          if (headers.includes(key))
            throw new VisibleError('Duplicate column header: ' + key);
          headers.push(key);
        }

        const errors = [];
        for (const rule of opts.headerErrors || []) {
          const output = await Promise.resolve(rule(headers));
          if (typeof output === 'string')
            throw new VisibleError(output);
        }

        const warnings = [];
        if (nNoHeader) {
          const pl = nNoHeader == 1 ? '' : 's';
          warnings.push('Found ' + nNoHeader + ' blank header' + pl + ' (column' + pl + ' will be ignored)');
        }
        for (const rule of opts.headerWarnings || []) {
          const output = rule(headers);
          if (typeof output === 'string')
            warnings.push(output);
        }
        if (warnings.length) {
          const confirmed = await this.$confirm({
            title: 'Are you sure?',
            msg: `Found ${this.$options.filters.pl(warnings.length, 'potential issue')}:<ul>${warnings.map(x => '<li>' + x + '</li>').join('')}</ul><br>Are you sure you want to continue?`
          });
          if (confirmed == 'No')
            return false;
        }
        return true;
      }

      const addItem = (item, rowI) => {
        if (!Object.values(item).some(x => (x != undefined) && (x != '')))
          // This row is empty
          return;
        if (!parsers)
          // Lazily initialize the parsers
          parsers = opts.parse ? Object.fromEntries(Object.keys(item).map(key => [ key, opts.parsers[key] || opts.parse ])) : opts.parsers;
        for (const [ key, parse ] of Object.entries(parsers))
          try {
            item[key] = parse(item[key]);
          } catch (e) {
            let msg = `<b>Import aborted</b><br>Error parsing value from <code>${key}</code> column in row ${rowI} – <i>${e.message}</i>`;
            if (item[key])
              msg += `<br><div class="mt-5 text-body-2 d-inline-block" style="max-height: 69px; overflow: auto; padding: 4px 8px; border: 1px solid rgba(255, 255, 255, 0.6);">${item[key] || '&nbsp;'}</div>`;
            throw new VisibleError(msg);
          }
        items.push(item);
      }

      const addWorkbook = async workbook => {
        const sheet = workbook.Sheets[ workbook.SheetNames[0] ];
        const range = XLS.utils.decode_range(sheet['!ref']);

        const row = [];
        for (let c = range.s.c; c <= range.e.c; c++)
          row.push(getExcelValue(sheet[ XLS.utils.encode_cell({ r: range.s.r, c }) ]));

        if (!opts.noHeaderRow)
          if (!(await addHeaders(row)))
            return false;

        const rows = [];
        const offset = range.s.c;
        for (let r = range.s.r + (opts.noHeaderRow ? 0 : 1); r <= range.e.r; r++) {
          for (let c = range.s.c; c <= range.e.c; c++)
            row[ c - offset ] = getExcelValue(sheet[ XLS.utils.encode_cell({ r, c }) ]);
          rows.push(Object.fromEntries(_.zip(headers, row)));
        }

        if (opts.preParse)
          await Promise.resolve(opts.preParse(headers, rows));

        for (const i in rows)
          addItem(rows[i], parseInt(i) + 2);

        return true;
      }

      if (file.name.endsWith('.csv')) {
        const CSV = (await import('@/utils/csv.js')).default;
        const data = await CSV.fetch({ file, noHeaderRow: !!opts.noHeaderRow });

        if (!opts.noHeaderRow)
          if (!(await addHeaders(data.fields)))
            return;

        const rows = [];
        for (const row of data.records)
          // TODO: consider how better to handle the case when a row has more values than the header
          rows.push(opts.noHeaderRow ? row : Object.fromEntries(_.zip(headers, row.slice(0, headers.length))));

        if (opts.preParse)
          await Promise.resolve(opts.preParse(headers, rows));

        for (const i in rows)
          addItem(rows[i], parseInt(i) + 2);
      }

      else if (file.name.endsWith('.xls')) {
        const tasks = [
          loadScript('/lib/xls-0.7.4-a.min.js'),
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => resolve(reader.result);
            reader.onerror = e => reject(e);
            reader.readAsBinaryString(file);
          })
        ];
        const content = (await Promise.all(tasks))[1];
        const workbook = XLS.parse_xlscfb(XLS.CFB.read(content, { type: 'binary' }));
        if (!(await addWorkbook(workbook)))
          return;
      }

      else if (file.name.endsWith('.xlsx')) {
        // Note that loading 'xlsx' depends on having 'jszip' script loaded
        await loadScript('/lib/jszip-0.8.0.min.js');
        const tasks = [
          loadScript('/lib/xlsx-0.8.0.min.js'),
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = e => resolve(reader.result);
            reader.onerror = e => reject(e);
            reader.readAsBinaryString(file);
          })
        ];
        const content = (await Promise.all(tasks))[1];
        const workbook = XLSX.read(content, { type: 'binary' });
        if (!(await addWorkbook(workbook)))
          return;
      }

      else {
        throw new VisibleError('File type is not supported (should be .csv, .xls, or .xlsx)');
      }

      return { headers, items };
    }
  }
};
